import { div, h1, h2, span, p, button, input, label, a, img, table, thead, tbody, tr, th, td, fnlink, fnstate } from '@srfnstack/fntags'
import { svg, rect, line, polyline, circle, text, title as svgTitle, g } from '@srfnstack/fntags/src/svgelements.mjs'

// Pinterest Stats: is the pinbot worth it? Everything comes from
// GET /api/pinterest/stats (our own tables, no Pinterest calls) for a date
// range: headline numbers, daily impressions and outbound clicks, top pins,
// boards, tribes, Etsy views of pinned vs unpinned listings and the approval
// funnel. Charts are plain inline SVG: one measure per chart, no second axis.

const PRESETS = [[7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '1 year']]

export const fmt = (v) => v === null || v === undefined ? '-' : Number(v).toLocaleString('en-US')
export const pct = (v, digits = 2) => v === null || v === undefined ? '-' : `${(v * 100).toFixed(digits)}%`
const perK = (v) => v === null || v === undefined ? '-' : v.toFixed(1)

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// shortDay renders "2026-10-07" as "Oct 7".
export const shortDay = (day) => {
  const d = new Date(`${day}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? day : `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`
}

const isoDay = (d) => d.toISOString().slice(0, 10)

// --- charts -------------------------------------------------------------------

const CHART_W = 640
const CHART_H = 160
const PAD = { top: 12, right: 8, bottom: 22, left: 40 }

// niceMax rounds a maximum up to 1, 2 or 5 times a power of ten.
export const niceMax = (v) => {
  if (!(v > 0)) return 1
  const pow = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 5, 10]) {
    if (v <= m * pow) return m * pow
  }
  return 10 * pow
}

const axis = (max, plotW, plotH, days) => {
  const ticks = [0, max / 2, max]
  const label = (v) => v >= 1000 ? `${+(v / 1000).toFixed(1)}k` : `${+v.toFixed(v < 10 ? 1 : 0)}`
  const every = Math.max(1, Math.ceil(days.length / 6))
  return g({},
    ...ticks.map(v => {
      const y = PAD.top + plotH - (v / max) * plotH
      return g({},
        line({ x1: PAD.left, x2: PAD.left + plotW, y1: y, y2: y, class: 'stroke-base-content', 'stroke-opacity': '0.12', 'stroke-width': '1' }),
        text({ x: PAD.left - 6, y: y + 4, 'text-anchor': 'end', class: 'fill-base-content', 'fill-opacity': '0.6', 'font-size': '11' }, label(v))
      )
    }),
    ...days.map((d, i) => i % every === 0
      ? text({ x: PAD.left + (i + 0.5) * (plotW / days.length), y: CHART_H - 6, 'text-anchor': 'middle', class: 'fill-base-content', 'fill-opacity': '0.6', 'font-size': '11' }, shortDay(d))
      : ''
    ).filter(Boolean)
  )
}

// DailyBars is one measure per day as bars; days still estimated by
// Pinterest are drawn lighter. Each bar has a native tooltip.
export const DailyBars = ({ daily, field, name }) => {
  const plotW = CHART_W - PAD.left - PAD.right
  const plotH = CHART_H - PAD.top - PAD.bottom
  const max = niceMax(Math.max(0, ...daily.map(d => d[field])))
  const slot = plotW / Math.max(1, daily.length)
  const barW = Math.max(1, Math.min(24, slot - 2))
  return svg({ viewBox: `0 0 ${CHART_W} ${CHART_H}`, class: 'w-full h-auto', role: 'img', 'aria-label': `${name} per day` },
    axis(max, plotW, plotH, daily.map(d => d.day)),
    ...daily.map((d, i) => {
      const h = (d[field] / max) * plotH
      const x = PAD.left + i * slot + (slot - barW) / 2
      return g({},
        // A hit target the full height of the day, so small bars are easy to hover.
        rect({ x: PAD.left + i * slot, y: PAD.top, width: slot, height: plotH, fill: 'transparent' },
          svgTitle(`${shortDay(d.day)}: ${fmt(d[field])} ${name.toLowerCase()}${d.estimated ? ' (estimated)' : ''}${d.pins ? `, ${d.pins} pinned` : ''}`)),
        h > 0
          ? rect({
            x,
            y: PAD.top + plotH - h,
            width: barW,
            height: h,
            rx: Math.min(2, barW / 2),
            class: 'fill-primary',
            'fill-opacity': d.estimated ? '0.4' : '1',
            'pointer-events': 'none'
          })
          : ''
      )
    }).filter(Boolean)
  )
}

// ViewsLines compares two series on one axis: Etsy views gained per listing
// per day for pinned and unpinned canvas listings.
export const ViewsLines = ({ daily }) => {
  const plotW = CHART_W - PAD.left - PAD.right
  const plotH = CHART_H - PAD.top - PAD.bottom
  const values = daily.flatMap(d => [d.pinned, d.unpinned]).filter(v => v !== null)
  const max = niceMax(Math.max(0, ...values))
  const slot = plotW / Math.max(1, daily.length)
  const xAt = (i) => PAD.left + (i + 0.5) * slot
  const yAt = (v) => PAD.top + plotH - (v / max) * plotH
  const series = (key, cls, dash) => {
    const pts = daily.map((d, i) => d[key] === null ? null : [xAt(i), yAt(d[key])]).filter(Boolean)
    return g({},
      pts.length > 1 ? polyline({ points: pts.map(p => p.join(',')).join(' '), fill: 'none', class: cls, 'stroke-width': '2', 'stroke-dasharray': dash || '', 'stroke-linejoin': 'round' }) : '',
      ...pts.map(([x, y]) => circle({ cx: x, cy: y, r: '3', class: cls.replace('stroke-', 'fill-') }))
    )
  }
  return svg({ viewBox: `0 0 ${CHART_W} ${CHART_H}`, class: 'w-full h-auto', role: 'img', 'aria-label': 'Etsy views per listing per day, pinned vs unpinned' },
    axis(max, plotW, plotH, daily.map(d => d.day)),
    series('unpinned', 'stroke-base-content', '4 3'),
    series('pinned', 'stroke-primary'),
    ...daily.map((d, i) => rect({ x: PAD.left + i * slot, y: PAD.top, width: slot, height: plotH, fill: 'transparent' },
      svgTitle(`${shortDay(d.day)}: pinned ${d.pinned === null ? '-' : d.pinned.toFixed(1)} (${d.pinned_listings} listings), unpinned ${d.unpinned === null ? '-' : d.unpinned.toFixed(1)} (${d.unpinned_listings} listings)`)
    ))
  )
}

// --- sections -----------------------------------------------------------------

const card = (...children) => div({ class: 'card bg-base-100 border border-base-300' }, div({ class: 'card-body p-4 gap-3' }, ...children))
const sectionTitle = (t, sub) => div({ class: 'flex flex-col gap-0.5' }, h2({ class: 'text-lg font-bold' }, t), sub ? span({ class: 'text-sm opacity-70' }, sub) : '')

const tile = (name, value, sub) => div({ class: 'rounded-box border border-base-300 bg-base-100 p-3 flex flex-col gap-1 min-w-0' },
  span({ class: 'text-xs uppercase tracking-wide opacity-60' }, name),
  span({ class: 'text-2xl font-bold tabular-nums' }, value),
  sub ? span({ class: 'text-xs opacity-60' }, sub) : ''
)

export const Headline = (s) => {
  const h = s.headline
  const est = s.estimated
  return div({ class: 'flex flex-col gap-2' },
    div({ class: 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3' },
      tile('Pins published', fmt(h.pins_published), `${fmt(h.pins_all_time)} all time`),
      tile('Impressions', fmt(h.impressions)),
      tile('Saves', fmt(h.saves), h.save_rate === null ? '' : `${pct(h.save_rate)} of impressions`),
      tile('Pin clicks', fmt(h.pin_clicks)),
      tile('Outbound clicks', fmt(h.outbound_clicks), 'visits to Etsy'),
      tile('Outbound click rate', pct(h.outbound_rate), 'outbound clicks / impressions')
    ),
    span({ class: 'text-xs opacity-70' },
      est.impressions > 0 || est.outbound_clicks > 0
        ? `Includes estimates from ${shortDay(est.from_day)} on (${fmt(est.impressions)} impressions, ${fmt(est.outbound_clicks)} outbound clicks): Pinterest finalizes each day after about 48 hours, so recent numbers can still move.`
        : 'All numbers in this range are final (Pinterest finalizes each day after about 48 hours).'
    )
  )
}

const tableWrap = (...children) => div({ class: 'overflow-x-auto -mx-4 px-4' }, table({ class: 'table table-sm' }, ...children))

const learnedCell = (l) => l
  ? span({ title: `posterior from ${l.pins} matured pins; 90% range ${perK(l.lo)} to ${perK(l.hi)}` }, `${perK(l.per_1k)} `, span({ class: 'opacity-60 text-xs' }, `(${perK(l.lo)} to ${perK(l.hi)})`))
  : span({ class: 'opacity-50' }, 'not yet')

export const TopPins = (pins) => pins.length === 0
  ? p({ class: 'text-sm opacity-60' }, 'No pins published by the end of this range.')
  : tableWrap(
    thead(tr(th(''), th('Pin'), th('Board'), th({ class: 'text-right' }, 'Age'), th({ class: 'text-right' }, 'Impr.'), th({ class: 'text-right' }, 'Saves'), th({ class: 'text-right' }, 'Outbound'), th('Links'))),
    tbody(...pins.map(pin => tr(
      td(pin.image_url
        ? img({ src: pin.image_url, alt: '', loading: 'lazy', class: 'w-12 h-12 object-cover rounded bg-base-200' })
        : div({ class: 'w-12 h-12 rounded bg-base-200' })),
      td({ class: 'min-w-48 max-w-xs' }, span({ class: 'line-clamp-2 text-sm' }, pin.title || '(untitled)')),
      td({ class: 'text-sm whitespace-nowrap' }, pin.board_name),
      td({ class: 'text-right tabular-nums whitespace-nowrap' }, `${pin.age_days}d`),
      td({ class: 'text-right tabular-nums' }, fmt(pin.impressions)),
      td({ class: 'text-right tabular-nums' }, fmt(pin.saves)),
      td({ class: 'text-right tabular-nums font-semibold' }, fmt(pin.outbound_clicks)),
      td({ class: 'whitespace-nowrap text-sm' },
        pin.pin_url ? a({ href: pin.pin_url, target: '_blank', rel: 'noopener', class: 'link link-primary mr-2' }, 'Pin') : '',
        pin.listing_url ? a({ href: pin.listing_url, target: '_blank', rel: 'noopener', class: 'link' }, 'Etsy') : ''
      )
    )))
  )

export const GroupTable = (rows, firstColumn) => rows.length === 0
  ? p({ class: 'text-sm opacity-60' }, 'Nothing pinned yet.')
  : tableWrap(
    thead(tr(th(firstColumn), th({ class: 'text-right' }, 'Pins'), th({ class: 'text-right' }, 'Impr.'), th({ class: 'text-right' }, 'Saves'), th({ class: 'text-right' }, 'Outbound'), th({ class: 'text-right' }, 'Rate'), th({ title: 'The learner\'s outbound clicks per 1,000 impressions, with its 90% range' }, 'Learned per 1k'))),
    tbody(...rows.map(r => tr(
      td({ class: 'text-sm' }, r.name),
      td({ class: 'text-right tabular-nums' }, fmt(r.pins)),
      td({ class: 'text-right tabular-nums' }, fmt(r.impressions)),
      td({ class: 'text-right tabular-nums' }, fmt(r.saves)),
      td({ class: 'text-right tabular-nums' }, fmt(r.outbound_clicks)),
      td({ class: 'text-right tabular-nums' }, pct(r.outbound_rate)),
      td({ class: 'text-sm whitespace-nowrap' }, learnedCell(r.learned))
    )))
  )

export const Funnel = (f) => {
  const reached = f.drafted - f.critic_rejected
  const steps = [
    ['Drafted', f.drafted, 'every draft the job wrote'],
    ['Rejected by the critic', f.critic_rejected, 'never shown to you'],
    ['Reached you', reached, 'carded for your review'],
    ['Approved by you', f.approved, 'including ones published or failed since'],
    ['Rejected by you', f.operator_rejected, ''],
    ['Still waiting', f.waiting, ''],
    ['Published', f.published, ''],
    ['Failed to publish', f.failed, 'retry or reject them in the Pin Queue']
  ]
  if (f.superseded) steps.push(['Closed as duplicates', f.superseded, 'superseded drafts of the same pin'])
  const max = Math.max(1, f.drafted)
  return div({ class: 'flex flex-col gap-1.5' },
    ...steps.map(([name, value, note]) => div({ class: 'grid grid-cols-[minmax(8rem,12rem)_1fr_3rem] items-center gap-2 text-sm' },
      span({ title: note }, name),
      div({ class: 'h-3 rounded bg-base-200 overflow-hidden' }, div({ class: 'h-full rounded bg-primary', style: { width: `${(value / max) * 100}%` } })),
      span({ class: 'text-right tabular-nums' }, fmt(value))
    ))
  )
}

export const EtsyViews = (v) => {
  const has = v.daily.some(d => d.pinned !== null || d.unpinned !== null)
  if (!has) return p({ class: 'text-sm opacity-60' }, 'No Etsy view snapshots in this range yet.')
  const summary = v.pinned_per_listing_day === null
    ? 'No pinned listing has view snapshots in this range yet.'
    : `Pinned listings gained ${v.pinned_per_listing_day.toFixed(2)} Etsy views a day each, unpinned ones ${v.unpinned_per_listing_day === null ? '-' : v.unpinned_per_listing_day.toFixed(2)}.`
  return div({ class: 'flex flex-col gap-2' },
    div({ class: 'flex flex-wrap gap-4 text-xs items-center' },
      span({ class: 'flex items-center gap-1.5' }, span({ class: 'inline-block w-4 h-0.5 bg-primary' }), 'Pinned listings'),
      span({ class: 'flex items-center gap-1.5' }, span({ class: 'inline-block w-4 border-t-2 border-dashed border-base-content opacity-70' }), 'Unpinned listings')
    ),
    ViewsLines({ daily: v.daily }),
    span({ class: 'text-sm' }, summary),
    span({ class: 'text-xs opacity-60' }, 'Canvas listings only. Not a controlled test: the listings that get pinned first are the most viewed ones, so compare the trend over weeks, not one day.')
  )
}

const Trends = (trends) => trends.length === 0
  ? p({ class: 'text-sm opacity-60' }, 'No Pinterest trend snapshot yet.')
  : div({ class: 'flex flex-wrap gap-2' },
    ...trends.map(t => span({ class: 'badge badge-outline gap-1' }, t.keyword, t.pct_growth_mom === null ? '' : span({ class: 'opacity-60' }, `${t.pct_growth_mom >= 0 ? '+' : ''}${t.pct_growth_mom}% month`)))
  )

export const WhatThisMeans = () => div({ class: 'alert alert-info items-start text-sm' },
  span({ class: 'material-symbols-rounded' }, 'info'),
  div({ class: 'flex flex-col gap-1' },
    span({ class: 'font-semibold' }, 'What this means'),
    span('Pinterest reach builds slowly: a pin keeps being shown for months, and an account usually needs 4 to 12 weeks of steady pinning before impressions and clicks climb. Low numbers in the first weeks are expected and say little.'),
    span('The number that matters is outbound clicks (people leaving Pinterest for the Etsy listing) and, over months, whether pinned listings gain more Etsy views than unpinned ones. Saves are an early sign a pin will keep getting shown.')
  )
)

// --- page ---------------------------------------------------------------------

export const PinterestStats = () => {
  const today = isoDay(new Date())
  const range = fnstate({ from: isoDay(new Date(Date.now() - 29 * 86400000)), to: today, preset: 30 })
  const stats = fnstate(null)
  const loading = fnstate(true)
  const error = fnstate('')

  const load = async () => {
    loading(true)
    error('')
    const { from, to } = range()
    try {
      const res = await fetch(`/api/pinterest/stats?from=${from}&to=${to}`)
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        stats(data)
      } else {
        error(data.error || `HTTP ${res.status}`)
      }
    } catch (err) {
      error(err.message)
    }
    loading(false)
  }
  range.subscribe(load)
  load()

  const setPreset = (days) => range({ from: isoDay(new Date(Date.now() - (days - 1) * 86400000)), to: isoDay(new Date()), preset: days })
  const setDate = (key, value) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) range({ ...range(), [key]: value, preset: null })
  }

  // Each section binds to the loaded stats on its own, so a reload swaps the
  // sections, never the range controls.
  const section = (render) => stats.bindAs(s => s ? render(s) : div())

  return div({ class: 'p-4 md:p-6 flex flex-col gap-4 max-w-7xl' },
    div({ class: 'flex flex-wrap items-center gap-3' },
      h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Pinterest Stats'),
      fnlink({ to: '/pin-queue', class: 'link text-sm' }, 'Pin Queue'),
      loading.bindAs(l => l ? span({ class: 'loading loading-dots loading-sm' }) : span())
    ),
    div({ class: 'flex flex-wrap items-end gap-2' },
      div({ class: 'join' },
        ...PRESETS.map(([days, name]) => button({
          class: range.bindAttr(r => `btn btn-sm join-item ${r.preset === days ? 'btn-active btn-primary' : ''}`),
          onclick: () => setPreset(days)
        }, name))
      ),
      label({ class: 'flex items-center gap-1 text-sm' }, 'From',
        input({ type: 'date', name: 'from', class: 'input input-bordered input-sm', max: today, value: range.bindAttr(r => r.from), onchange: e => setDate('from', e.target.value) })),
      label({ class: 'flex items-center gap-1 text-sm' }, 'To',
        input({ type: 'date', name: 'to', class: 'input input-bordered input-sm', max: today, value: range.bindAttr(r => r.to), onchange: e => setDate('to', e.target.value) }))
    ),
    error.bindAs(e => e ? div({ class: 'alert alert-error text-sm', role: 'alert' }, `Could not load the stats: ${e}`) : span()),
    WhatThisMeans(),
    section(Headline),
    section(s => card(
      sectionTitle('Impressions per day', 'Lighter bars are days Pinterest still estimates.'),
      DailyBars({ daily: s.daily, field: 'impressions', name: 'Impressions' })
    )),
    section(s => card(
      sectionTitle('Outbound clicks per day', 'Clicks from a pin through to the Etsy listing.'),
      DailyBars({ daily: s.daily, field: 'outbound_clicks', name: 'Outbound clicks' })
    )),
    section(s => card(sectionTitle('Top pins', 'By outbound clicks in the range, then saves and impressions.'), TopPins(s.top_pins))),
    section(s => div({ class: 'grid grid-cols-1 xl:grid-cols-2 gap-4' },
      card(sectionTitle('Boards'), GroupTable(s.boards, 'Board')),
      card(sectionTitle('Tribes'), GroupTable(s.tribes, 'Tribe'))
    )),
    section(s => card(sectionTitle('Etsy views: pinned vs unpinned', 'Views gained per listing per day.'), EtsyViews(s.etsy_views))),
    section(s => div({ class: 'grid grid-cols-1 xl:grid-cols-2 gap-4' },
      card(sectionTitle('Approval funnel', 'Drafts written in this range and where they ended up.'), Funnel(s.funnel)),
      card(
        sectionTitle('Learner and trends'),
        s.learned
          ? span({ class: 'text-sm' }, `Across all matured pins the learner puts outbound clicks at ${perK(s.learned.per_1k)} per 1,000 impressions (90% range ${perK(s.learned.lo)} to ${perK(s.learned.hi)}, ${fmt(s.learned.pins)} pins; ${fmt(s.learned.young_pins)} pins still too young to count).`)
          : span({ class: 'text-sm opacity-60' }, 'The learner has not scored any pins yet; pins count once they are a few weeks old.'),
        span({ class: 'text-xs uppercase tracking-wide opacity-60 mt-2' }, 'Fastest growing Pinterest trends'),
        Trends(s.trends)
      )
    ))
  )
}
